import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { LineChart, BarChart, PieChart } from 'react-native-chart-kit';

import { useTheme } from '../../theme/ThemeContext';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { Button } from '../common/Button';
import type { AppDispatch } from '../../store';
import {
  fetchGroupProgressInsights,
  selectGroupProgressInsights,
  selectGroupInsightsByEngagement,
  selectStudyGroupLoading,
  selectStudyGroupError,
  type GroupProgressInsight,
  type EngagementTrend,
  type ProgressRecommendation,
} from '../../store/slices/studyGroupSlice';

interface GroupProgressTrackerProps {
  groupId: string;
  showDetailedAnalytics?: boolean;
  onRecommendationSelect?: (recommendation: ProgressRecommendation) => void;
}

const screenWidth = Dimensions.get('window').width;

export const GroupProgressTracker: React.FC<GroupProgressTrackerProps> = ({
  groupId,
  showDetailedAnalytics = true,
  onRecommendationSelect,
}) => {
  const { theme } = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const progressInsights = useSelector(selectGroupProgressInsights);
  const insightsByEngagement = useSelector(selectGroupInsightsByEngagement);
  const loading = useSelector(selectStudyGroupLoading);
  const error = useSelector(selectStudyGroupError);

  const [selectedView, setSelectedView] = useState<
    'overview' | 'trends' | 'members' | 'recommendations'
  >('overview');

  const groupInsight = progressInsights[groupId];

  useEffect(() => {
    dispatch(fetchGroupProgressInsights(groupId));
  }, [dispatch, groupId]);

  const getProgressColor = (progress: number) => {
    if (progress >= 0.8) return theme.colors.success;
    if (progress >= 0.5) return theme.colors.warning;
    return theme.colors.error;
  };

  const getEngagementIcon = (level: number) => {
    if (level >= 0.8) return 'trending-up';
    if (level >= 0.5) return 'remove';
    return 'trending-down';
  };

  const formatPercentage = (value: number) => `${Math.round(value * 100)}%`;

  const renderOverview = () => {
    if (!groupInsight) return null;

    const memberProgressData = Object.entries(
      groupInsight.memberProgressDistribution
    ).map(([memberId, progress]) => ({
      name: `Member ${memberId.slice(-4)}`,
      progress: Math.round(progress * 100),
      color: getProgressColor(progress),
    }));

    return (
      <ScrollView
        style={styles.viewContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Overall Progress */}
        <View style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
            Overall Group Progress
          </Text>
          <View style={styles.progressOverview}>
            <View style={styles.progressCircle}>
              <ConfidenceIndicator
                confidence={groupInsight.overallProgress}
                size='large'
                showLabel={true}
                label={formatPercentage(groupInsight.overallProgress)}
              />
            </View>
            <View style={styles.progressStats}>
              <View style={styles.statRow}>
                <Ionicons name='flash' size={16} color={theme.colors.primary} />
                <Text
                  style={[
                    styles.statLabel,
                    { color: theme.colors.textSecondary },
                  ]}
                >
                  Learning Velocity
                </Text>
                <Text style={[styles.statValue, { color: theme.colors.text }]}>
                  {formatPercentage(groupInsight.learningVelocity)}
                </Text>
              </View>
              <View style={styles.statRow}>
                <Ionicons
                  name='people'
                  size={16}
                  color={theme.colors.primary}
                />
                <Text
                  style={[
                    styles.statLabel,
                    { color: theme.colors.textSecondary },
                  ]}
                >
                  Collaboration
                </Text>
                <Text style={[styles.statValue, { color: theme.colors.text }]}>
                  {formatPercentage(groupInsight.collaborationEffectiveness)}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Member Progress Distribution */}
        <View style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
            Member Progress Distribution
          </Text>
          <BarChart
            data={{
              labels: memberProgressData.map(m => m.name),
              datasets: [
                {
                  data: memberProgressData.map(m => m.progress),
                },
              ],
            }}
            width={screenWidth - 64}
            height={200}
            yAxisLabel=''
            yAxisSuffix='%'
            chartConfig={{
              backgroundColor: theme.colors.surface,
              backgroundGradientFrom: theme.colors.surface,
              backgroundGradientTo: theme.colors.surface,
              decimalPlaces: 0,
              color: (opacity = 1) => `rgba(0, 122, 255, ${opacity})`,
              labelColor: (opacity = 1) => theme.colors.textSecondary,
              style: {
                borderRadius: 16,
              },
            }}
            style={styles.chart}
          />
        </View>

        {/* Skill Growth Areas */}
        <View style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
            Skill Growth Areas
          </Text>
          <View style={styles.skillGrowthAreas}>
            {groupInsight.skillGrowthAreas.map((skill, index) => (
              <View key={index} style={styles.skillGrowthItem}>
                <View
                  style={[
                    styles.skillGrowthDot,
                    { backgroundColor: theme.colors.success },
                  ]}
                />
                <Text
                  style={[styles.skillGrowthText, { color: theme.colors.text }]}
                >
                  {skill.replace(/_/g, ' ')}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* Collective Achievements */}
        {groupInsight.collectiveAchievements.length > 0 && (
          <View
            style={[styles.card, { backgroundColor: theme.colors.surface }]}
          >
            <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
              Recent Achievements
            </Text>
            <View style={styles.achievements}>
              {groupInsight.collectiveAchievements
                .slice(0, 3)
                .map(achievement => (
                  <View key={achievement.id} style={styles.achievementItem}>
                    <Ionicons
                      name='trophy'
                      size={20}
                      color={theme.colors.warning}
                    />
                    <View style={styles.achievementInfo}>
                      <Text
                        style={[
                          styles.achievementTitle,
                          { color: theme.colors.text },
                        ]}
                      >
                        {achievement.title}
                      </Text>
                      <Text
                        style={[
                          styles.achievementDescription,
                          { color: theme.colors.textSecondary },
                        ]}
                      >
                        {achievement.description}
                      </Text>
                    </View>
                  </View>
                ))}
            </View>
          </View>
        )}
      </ScrollView>
    );
  };

  const renderTrends = () => {
    if (!groupInsight || groupInsight.engagementTrends.length === 0)
      return null;

    const trendData = {
      labels: groupInsight.engagementTrends.map(trend => trend.period),
      datasets: [
        {
          data: groupInsight.engagementTrends.map(
            trend => trend.engagement * 100
          ),
          color: (opacity = 1) => `rgba(0, 122, 255, ${opacity})`,
          strokeWidth: 2,
        },
        {
          data: groupInsight.engagementTrends.map(
            trend => trend.collaboration * 100
          ),
          color: (opacity = 1) => `rgba(52, 199, 89, ${opacity})`,
          strokeWidth: 2,
        },
      ],
      legend: ['Engagement', 'Collaboration'],
    };

    return (
      <ScrollView
        style={styles.viewContainer}
        showsVerticalScrollIndicator={false}
      >
        <View style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
            Engagement & Collaboration Trends
          </Text>
          <LineChart
            data={trendData}
            width={screenWidth - 64}
            height={220}
            yAxisSuffix='%'
            chartConfig={{
              backgroundColor: theme.colors.surface,
              backgroundGradientFrom: theme.colors.surface,
              backgroundGradientTo: theme.colors.surface,
              decimalPlaces: 0,
              color: (opacity = 1) => `rgba(0, 122, 255, ${opacity})`,
              labelColor: (opacity = 1) => theme.colors.textSecondary,
              style: {
                borderRadius: 16,
              },
            }}
            style={styles.chart}
          />
        </View>

        {/* Trend Analysis */}
        <View style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
            Trend Analysis
          </Text>
          {groupInsight.engagementTrends.map((trend, index) => (
            <View key={index} style={styles.trendItem}>
              <Text style={[styles.trendPeriod, { color: theme.colors.text }]}>
                {trend.period}
              </Text>
              <View style={styles.trendMetrics}>
                <View style={styles.trendMetric}>
                  <Ionicons
                    name={getEngagementIcon(trend.engagement)}
                    size={16}
                    color={getProgressColor(trend.engagement)}
                  />
                  <Text
                    style={[
                      styles.trendValue,
                      { color: theme.colors.textSecondary },
                    ]}
                  >
                    {formatPercentage(trend.engagement)} engagement
                  </Text>
                </View>
                <View style={styles.trendMetric}>
                  <Ionicons
                    name={getEngagementIcon(trend.satisfaction)}
                    size={16}
                    color={getProgressColor(trend.satisfaction)}
                  />
                  <Text
                    style={[
                      styles.trendValue,
                      { color: theme.colors.textSecondary },
                    ]}
                  >
                    {formatPercentage(trend.satisfaction)} satisfaction
                  </Text>
                </View>
              </View>
            </View>
          ))}
        </View>
      </ScrollView>
    );
  };

  const renderMembers = () => {
    if (!groupInsight) return null;

    const memberData = Object.entries(
      groupInsight.memberProgressDistribution
    ).map(([memberId, progress]) => ({
      id: memberId,
      name: `Member ${memberId.slice(-4)}`,
      progress,
      color: getProgressColor(progress),
    }));

    return (
      <ScrollView
        style={styles.viewContainer}
        showsVerticalScrollIndicator={false}
      >
        <View style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
            Individual Member Progress
          </Text>
          {memberData.map(member => (
            <View key={member.id} style={styles.memberItem}>
              <View style={styles.memberInfo}>
                <Text style={[styles.memberName, { color: theme.colors.text }]}>
                  {member.name}
                </Text>
                <Text style={[styles.memberProgress, { color: member.color }]}>
                  {formatPercentage(member.progress)}
                </Text>
              </View>
              <View style={styles.memberProgressBar}>
                <View
                  style={[
                    styles.memberProgressFill,
                    {
                      backgroundColor: member.color,
                      width: `${member.progress * 100}%`,
                    },
                  ]}
                />
              </View>
            </View>
          ))}
        </View>
      </ScrollView>
    );
  };

  const renderRecommendations = () => {
    if (!groupInsight || groupInsight.recommendations.length === 0) return null;

    const sortedRecommendations = [...groupInsight.recommendations].sort(
      (a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      }
    );

    return (
      <ScrollView
        style={styles.viewContainer}
        showsVerticalScrollIndicator={false}
      >
        <View style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.cardTitle, { color: theme.colors.text }]}>
            AI Recommendations
          </Text>
          {sortedRecommendations.map((recommendation, index) => (
            <TouchableOpacity
              key={index}
              style={styles.recommendationItem}
              onPress={() => onRecommendationSelect?.(recommendation)}
            >
              <View style={styles.recommendationHeader}>
                <View style={styles.recommendationInfo}>
                  <Text
                    style={[
                      styles.recommendationTitle,
                      { color: theme.colors.text },
                    ]}
                  >
                    {recommendation.title}
                  </Text>
                  <View style={styles.recommendationMeta}>
                    <View
                      style={[
                        styles.priorityBadge,
                        {
                          backgroundColor:
                            recommendation.priority === 'high'
                              ? theme.colors.errorLight
                              : recommendation.priority === 'medium'
                                ? theme.colors.warningLight
                                : theme.colors.successLight,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.priorityText,
                          {
                            color:
                              recommendation.priority === 'high'
                                ? theme.colors.error
                                : recommendation.priority === 'medium'
                                  ? theme.colors.warning
                                  : theme.colors.success,
                          },
                        ]}
                      >
                        {recommendation.priority}
                      </Text>
                    </View>
                    <Text
                      style={[
                        styles.recommendationType,
                        { color: theme.colors.textSecondary },
                      ]}
                    >
                      {recommendation.type.replace(/_/g, ' ')}
                    </Text>
                  </View>
                </View>
                <Ionicons
                  name='chevron-forward'
                  size={20}
                  color={theme.colors.textSecondary}
                />
              </View>
              <Text
                style={[
                  styles.recommendationDescription,
                  { color: theme.colors.textSecondary },
                ]}
              >
                {recommendation.description}
              </Text>
              <View style={styles.recommendationMetrics}>
                <View style={styles.metricItem}>
                  <Ionicons
                    name='trending-up'
                    size={14}
                    color={theme.colors.primary}
                  />
                  <Text
                    style={[
                      styles.metricText,
                      { color: theme.colors.textSecondary },
                    ]}
                  >
                    {formatPercentage(recommendation.expectedImpact)} impact
                  </Text>
                </View>
                <View style={styles.metricItem}>
                  <Ionicons
                    name='time'
                    size={14}
                    color={theme.colors.primary}
                  />
                  <Text
                    style={[
                      styles.metricText,
                      { color: theme.colors.textSecondary },
                    ]}
                  >
                    {recommendation.implementationEffort < 0.3
                      ? 'Low'
                      : recommendation.implementationEffort < 0.7
                        ? 'Medium'
                        : 'High'}{' '}
                    effort
                  </Text>
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>
    );
  };

  const renderViewSelector = () => {
    const views = [
      { key: 'overview', label: 'Overview', icon: 'analytics' },
      { key: 'trends', label: 'Trends', icon: 'trending-up' },
      { key: 'members', label: 'Members', icon: 'people' },
      { key: 'recommendations', label: 'AI Tips', icon: 'bulb' },
    ];

    return (
      <View style={styles.viewSelector}>
        {views.map(view => (
          <TouchableOpacity
            key={view.key}
            style={[
              styles.viewButton,
              selectedView === view.key && {
                backgroundColor: theme.colors.primaryLight,
              },
            ]}
            onPress={() => setSelectedView(view.key as any)}
          >
            <Ionicons
              name={view.icon as any}
              size={16}
              color={
                selectedView === view.key
                  ? theme.colors.primary
                  : theme.colors.textSecondary
              }
            />
            <Text
              style={[
                styles.viewButtonText,
                {
                  color:
                    selectedView === view.key
                      ? theme.colors.primary
                      : theme.colors.textSecondary,
                },
              ]}
            >
              {view.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  if (loading.insights) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingSpinner />
        <Text
          style={[styles.loadingText, { color: theme.colors.textSecondary }]}
        >
          Analyzing group progress...
        </Text>
      </View>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error}
        onRetry={() => dispatch(fetchGroupProgressInsights(groupId))}
      />
    );
  }

  if (!groupInsight) {
    return (
      <View style={styles.emptyContainer}>
        <Ionicons
          name='analytics-outline'
          size={64}
          color={theme.colors.textSecondary}
        />
        <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
          No progress data available
        </Text>
        <Text
          style={[styles.emptySubtext, { color: theme.colors.textSecondary }]}
        >
          Group progress insights will appear as members engage with activities
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {renderViewSelector()}
      {selectedView === 'overview' && renderOverview()}
      {selectedView === 'trends' && renderTrends()}
      {selectedView === 'members' && renderMembers()}
      {selectedView === 'recommendations' && renderRecommendations()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  viewSelector: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  viewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 12,
  },
  viewButtonText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  viewContainer: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 32,
  },
  loadingText: {
    fontSize: 16,
    marginTop: 16,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    textAlign: 'center',
    paddingHorizontal: 32,
  },
  card: {
    borderRadius: 12,
    padding: 16,
    margin: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 16,
  },
  progressOverview: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  progressCircle: {
    marginRight: 24,
  },
  progressStats: {
    flex: 1,
  },
  statRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  statLabel: {
    fontSize: 14,
    marginLeft: 8,
    flex: 1,
  },
  statValue: {
    fontSize: 14,
    fontWeight: '600',
  },
  chart: {
    marginVertical: 8,
    borderRadius: 16,
  },
  skillGrowthAreas: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  skillGrowthItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
    marginBottom: 8,
  },
  skillGrowthDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  skillGrowthText: {
    fontSize: 14,
    textTransform: 'capitalize',
  },
  achievements: {
    marginTop: 8,
  },
  achievementItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  achievementInfo: {
    flex: 1,
    marginLeft: 12,
  },
  achievementTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  achievementDescription: {
    fontSize: 12,
    lineHeight: 16,
  },
  trendItem: {
    marginBottom: 16,
  },
  trendPeriod: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  trendMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  trendMetric: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  trendValue: {
    fontSize: 12,
    marginLeft: 4,
  },
  memberItem: {
    marginBottom: 16,
  },
  memberInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  memberName: {
    fontSize: 14,
    fontWeight: '500',
  },
  memberProgress: {
    fontSize: 14,
    fontWeight: '600',
  },
  memberProgressBar: {
    height: 4,
    backgroundColor: 'rgba(0,0,0,0.1)',
    borderRadius: 2,
  },
  memberProgressFill: {
    height: '100%',
    borderRadius: 2,
  },
  recommendationItem: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  recommendationHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  recommendationInfo: {
    flex: 1,
  },
  recommendationTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  recommendationMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  priorityBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    marginRight: 8,
  },
  priorityText: {
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  recommendationType: {
    fontSize: 12,
    textTransform: 'capitalize',
  },
  recommendationDescription: {
    fontSize: 12,
    lineHeight: 16,
    marginBottom: 8,
  },
  recommendationMetrics: {
    flexDirection: 'row',
  },
  metricItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
  metricText: {
    fontSize: 11,
    marginLeft: 4,
  },
});
